import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useSound from "use-sound";
import { EmailField, PasswordField } from "./Signup.style";
import "../../../Styles/authentication.css";
import "../../../Styles/typography.css";

import { ReactComponent as HeaderLogo } from "../../../Assets/Images/LandingPage/subjects-master-logo.svg";
import { ReactComponent as ButtonArrow } from "../../../Assets/Images/Button/btn_arrow.svg";
import { ReactComponent as FacebookIcon } from "../../../Assets/Images/Header/Facebook.svg";
import { ReactComponent as InstaIcon } from "../../../Assets/Images/Header/Instagram.svg";
import { ReactComponent as TiktokIcon } from "../../../Assets/Images/Header/TikTok.svg";
import { SOCIAL_MEDIA_LINKS } from "Helpers";
import EmailSuccessModal from "Components/Commons/EmailSuccessModal";

import { InputAdornment, IconButton, Typography, InputLabel, Box, Select, MenuItem, FormControl } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Visibility from "@material-ui/icons/Visibility";

// FORM VALIDATION
import { useFormik } from "formik";
import * as Yup from "yup";

// CONST
import { Api, CODES, URL_LOGIN, URL_GENERAL_DASHBOARD, AGE_RANGE, } from "Helpers";
import { showToast } from "Redux/General/Actions";

// SOUNDS
import LoginSound from "Assets/Sounds/login-sound.mp3";
import CustomButton from "Components/Commons/CustomButton/CustomButton";
import CaptchaWidget from "Components/Commons/Captcha/CaptchaWidget";
import { captchaHeaders } from "Helpers/captcha";
import MobileHeader from "Components/Header/MobileHeader";

function Login() {
	const [isLoading, setIsLoading] = useState(false);
	const [image, setImage] = useState(null);
	const [customImage, setStatusOfCustomImage] = useState(false);
	const [isSignUpSuccess, setIsSignUpSuccess] = useState(false);

	const [values, setValues] = useState({
		password: "",
		confirmPassword: "",
		showPassword: false,
		showConfirmPassword: false,
	});

	const [play] = useSound(LoginSound, { volume: 0.2 });

	// REDUX
	const isLoggedIn = useSelector((state) => state.Auth.isLoggedIn);
	const dispatch = useDispatch();

	// ROUTER
	const navigate = useNavigate();

	useEffect(() => {
		checkIfAlreadyLoggedIn();
		// eslint-disable-next-line
	}, []);

	//form
	const formik = useFormik({
		initialValues: {
			firstName: "",
			lastName: "",
			email: "",
			mobileNumber: "",
			ageRange: "",
			schoolName: "",
			password: "",
			confirmPassword: "",
		},
		validationSchema: Yup.object({
			firstName: Yup.string().trim().required("*Please enter your first name").min(2, "First name must be at least 2 characters"),
			lastName: Yup.string().trim().required("*Please enter your last name").min(2, "Last name must be at least 2 characters"),
			email: Yup.string().required("*Please enter your email address").email("Please enter a valid email address"),
			mobileNumber: Yup.string()
				.required("*Please enter your mobile number")
				.matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
			ageRange: Yup.string().required("*Please select your age range"),
			schoolName: Yup.string().trim().required("*Please enter your school name").min(2, "School name must be at least 2 characters"),
			password: Yup.string().trim().required("*Please enter your password").min(6, "Password must be at least 6 characters"),
			confirmPassword: Yup.string()
				.required("*Please confirm your password")
				.oneOf([Yup.ref("password"), null], "Passwords must match"),
		}),
		onSubmit: (values) => {
			handleRegister(values);
		},
	});

	const captchaRef = useRef(null);

	function checkIfAlreadyLoggedIn() {
		if (isLoggedIn) navigate(URL_GENERAL_DASHBOARD);
	}

	const handleClickShowPassword = () => {
		setValues({ ...values, showPassword: !values.showPassword });
	};

	const handleClickShowConfirmPassword = () => {
		setValues({ ...values, showConfirmPassword: !values.showConfirmPassword });
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	async function handleRegister(values) {
		setIsLoading(true);

		let captchaToken = null;
		try {
			captchaToken = await captchaRef.current?.getToken();
		} catch (captchaError) {
			dispatch(showToast(captchaError.message));
			setIsLoading(false);
			return;
		}

		// Remove confirmPassword before sending to API
		const { confirmPassword, ...registrationData } = values;

		let data = new FormData();

		// Always set user details
		registrationData.isActive = true;
		registrationData.isEmailVerify = false;

		if (image) {
			// User-selected image
			registrationData.isAvatarSelect = !customImage ? true : false;
			data.append("multipartFile", image);
		} else {
			// Dummy file (1x1 transparent PNG as Blob)
			const dummyFile = new File(
				[new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])], // PNG header
				"dummy.png",
				{ type: "image/png" }
			);
			registrationData.isAvatarSelect = false;
			data.append("multipartFile", dummyFile);
		}

		data.append(
			"userReqDto",
			new Blob([JSON.stringify(registrationData)], {
				type: "application/json",
			})
		);

		new Api()
			.post("user/sign-up", { data, headers: captchaHeaders(captchaToken), returnError: true })
			.then(async (response) => {
				if (response.status === 200 || response.status === 201) {
					setIsSignUpSuccess(true);
					setIsLoading(false);
				} else {
					dispatch(showToast(response.data.message || "Registration failed"));
					setIsLoading(false);
				}
			})
			.catch((error) => {
				if (error.status === CODES.UNAUTHORIZED) dispatch(showToast(error.data.message || "Registration failed"));
				setIsLoading(false);
			})
			.finally(() => captchaRef.current?.reset());
	}

	return (
		<div className="login-regi-wrapper">
			<div className="auth-wrap-left">
				<div className="auth_wrap_logo">
					<MobileHeader></MobileHeader>
					<Link to="/" className="desktop_logo_login">
						<HeaderLogo></HeaderLogo>
					</Link>
				</div>
				<div className="auth_wrap_left_in">
					<h1>
						Welcome to <br /> <span className="redfont">Subjects Master</span>
					</h1>
					<p>
						Create an account and start exploring Subjects Master today! Access fun games, interactive activities, and learning challenges that make mastering subjects exciting and
						rewarding. Perfect for students, parents, and lifelong learners alike.
					</p>
				</div>
			</div>
			<div className="auth_wrap_right">
				<div className="">
					<div className="menu_other_buttons">
						<div className="social_handles">
							<ul>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.FACEBOOK} target="_blank" rel="noopener noreferrer">
										<FacebookIcon />
									</a>
								</li>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer">
										<InstaIcon />
									</a>
								</li>
								<li>
									<a href={SOCIAL_MEDIA_LINKS.TIKTOK} target="_blank" rel="noopener noreferrer">
										<TiktokIcon />
									</a>
								</li>
							</ul>
						</div>

						<div className="portal_login_link">
							<Link to="/teacher/login">Portal Login</Link>
						</div>
						<div className="logins_btn">
							<Link to="/login" className="customs_btn">
								Login{" "}
								<span>
									<ButtonArrow />
								</span>
							</Link>
						</div>
					</div>
				</div>
				<div className="auth_right_inner">
					<div className="auth_right_head">
						<h2>Register</h2>
						<h4 className="redfont">Start Your Learning Journey Today</h4>
					</div>
					<form onSubmit={formik.handleSubmit}>
						<Box className="content">
							<Box className="header">
								<InputLabel className="header"></InputLabel>
							</Box>
							<Box>
								<Box className="flex-column-parent">
									{/* Row 1: First Name and Last Name */}
									<Box className="form-row" style={{ display: "flex", gap: "12px", marginBottom: "15px" }}>
										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													First Name
												</InputLabel>
											</Box>
											<EmailField
												id="firstName"
												name="firstName"
												className="login compact-field"
												autoComplete="false"
												onBlur={formik.handleBlur}
												onChange={formik.handleChange}
												value={formik.values.firstName}
												placeholder="Enter first name"
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.firstName && formik.errors.firstName ? formik.errors.firstName : ""}
											</Box>
										</Box>

										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Last Name
												</InputLabel>
											</Box>
											<EmailField
												id="lastName"
												name="lastName"
												className="login compact-field"
												autoComplete="false"
												onBlur={formik.handleBlur}
												onChange={formik.handleChange}
												value={formik.values.lastName}
												placeholder="Enter last name"
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : ""}
											</Box>
										</Box>
									</Box>

									{/* Row 2: Email and Mobile Number */}
									<Box className="form-row" style={{ display: "flex", gap: "12px", marginBottom: "15px" }}>
										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Email Address
												</InputLabel>
											</Box>
											<EmailField
												id="email"
												name="email"
												className="login compact-field"
												autoComplete="false"
												onBlur={formik.handleBlur}
												onChange={formik.handleChange}
												value={formik.values.email}
												placeholder="Enter email address"
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.email && formik.errors.email ? formik.errors.email : ""}
											</Box>
										</Box>

										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Mobile Number
												</InputLabel>
											</Box>
											<EmailField
												id="mobileNumber"
												name="mobileNumber"
												className="login compact-field"
												autoComplete="false"
												onBlur={formik.handleBlur}
												onChange={formik.handleChange}
												value={formik.values.mobileNumber}
												placeholder="Enter mobile number"
												type="tel"
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.mobileNumber && formik.errors.mobileNumber ? formik.errors.mobileNumber : ""}
											</Box>
										</Box>
									</Box>

									{/* Row 3: Age Range and School Name */}
									<Box className="form-row" style={{ display: "flex", gap: "12px", marginBottom: "15px" }}>
										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Age Range
												</InputLabel>
											</Box>
											<FormControl
												className="login compact-field"
												style={{
													width: "100%",
													height: "40px",
												}}
											>
												<Select
													id="ageRange"
													name="ageRange"
													value={formik.values.ageRange}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													displayEmpty
													className="age-select-custom"
													IconComponent={KeyboardArrowDownIcon}
													style={{
														height: "40px",
														fontSize: "14px",
														backgroundColor: "white",
													}}
													inputProps={{
														style: {
															padding: "8px 12px",
															fontSize: "14px",
															height: "24px",
															display: "flex",
															alignItems: "center",
														},
													}}
													MenuProps={{
														PaperProps: {
															style: {
																maxHeight: 200,
																marginTop: 5,
															},
														},
													}}
												>
													<MenuItem value="" disabled style={{ fontSize: "14px", color: "#999" }}>
														Select age range
													</MenuItem>
													{AGE_RANGE.map((range) => (
														<MenuItem key={range.value} value={range.value} style={{ fontSize: "14px" }}>
															{range.display}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.ageRange && formik.errors.ageRange ? formik.errors.ageRange : ""}
											</Box>
										</Box>

										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="Box" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													School Name
												</InputLabel>
											</Box>
											<EmailField
												id="schoolName"
												name="schoolName"
												className="login compact-field"
												autoComplete="false"
												onBlur={formik.handleBlur}
												onChange={formik.handleChange}
												value={formik.values.schoolName}
												placeholder="Enter school name"
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.schoolName && formik.errors.schoolName ? formik.errors.schoolName : ""}
											</Box>
										</Box>
									</Box>

									{/* Row 4: Password and Confirm Password */}
									<Box className="form-row" style={{ display: "flex", gap: "12px", marginBottom: "15px" }}>
										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="div" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Password
												</InputLabel>
											</Box>
											<PasswordField
												placeholder="Enter password"
												name="password"
												key="password"
												className="login compact-field"
												value={formik.values.password}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												type={values.showPassword ? "text" : "password"}
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
												endAdornment={
													<InputAdornment position="end" name="password" key="password">
														<IconButton
															aria-label="toggle password visibility"
															onClick={handleClickShowPassword}
															onMouseDown={handleMouseDownPassword}
															className="paswword-icon"
															size="small"
															style={{ padding: "4px" }}
														>
															{values.showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
														</IconButton>
													</InputAdornment>
												}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.password && formik.errors.password ? formik.errors.password : ""}
											</Box>
										</Box>

										<Box className="flex-column field_wrapper" style={{ flex: 1 }}>
											<Box component="div" sx={{ display: "inline" }} className="text">
												<InputLabel className="text" style={{ fontSize: "14px", marginBottom: "5px" }}>
													Confirm Password
												</InputLabel>
											</Box>
											<PasswordField
												placeholder="Confirm password"
												name="confirmPassword"
												key="confirmPassword"
												className="login compact-field"
												value={formik.values.confirmPassword}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												type={values.showConfirmPassword ? "text" : "password"}
												style={{
													height: "40px",
													"& .MuiInputBase-input": {
														padding: "8px 12px",
														fontSize: "14px",
													},
												}}
												endAdornment={
													<InputAdornment position="end" name="confirmPassword" key="confirmPassword">
														<IconButton
															aria-label="toggle confirm password visibility"
															onClick={handleClickShowConfirmPassword}
															onMouseDown={handleMouseDownPassword}
															className="paswword-icon"
															size="small"
															style={{ padding: "4px" }}
														>
															{values.showConfirmPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
														</IconButton>
													</InputAdornment>
												}
											/>
											<Box component="div" sx={{ display: "inline", height: "8px" }} className="validation-error text" style={{ fontSize: "12px" }}>
												{formik.touched.confirmPassword && formik.errors.confirmPassword ? formik.errors.confirmPassword : ""}
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
							<CaptchaWidget ref={captchaRef} action="sign_up" />
							<div className="login_btn_divs">
								<Box className="login-button-parent">
									<CustomButton text={isLoading ? "Loading..." : "Register"} className="button" type="submit" isLoading={isLoading} />
								</Box>

								<Box className="model-footer ac_info_foter">
									<Typography className="font dose-not-have-account-font">Already have an account ?</Typography>
									<Typography className="font register-as-school-font">
										<Link to={URL_LOGIN}>Login</Link>
									</Typography>
								</Box>
							</div>
						</Box>
					</form>
					<EmailSuccessModal
						open={isSignUpSuccess}
						handleClose={() => {
							setIsSignUpSuccess(!isSignUpSuccess);
							navigate(URL_LOGIN);
						}}
					/>
				</div>
			</div>
		</div>
	);
}

export default Login;
